import { InformationButton } from "@components/buttons/information";
import {
  SHAutocomplete,
  SHRadio,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import { useInvestment } from "@hooks/useInvestment";
import { useManagedAccount } from "@hooks/useManagedAccount";
import {
  PortfolioTemplateFeeCalculatorDataDTO,
  SelectedInvestmentDTO,
  SelectedPortfolioTemplateDTO,
} from "@models/managed-accounts/entities/step/setup";
import { RootState } from "@redux/store";
import { useSelector } from "react-redux";
import { useEffectOnce } from "react-use";

interface PortfolioTemplateProps {
  isDisabled?: boolean;
  portfolioSize: number;
  replaceInvestment?: (value: SelectedInvestmentDTO[]) => void;
  onReplaceData?: (
    investments: SelectedInvestmentDTO[],
    feeCalculators: PortfolioTemplateFeeCalculatorDataDTO[],
  ) => void;
  onClearSaveAsTemplateWarnings: () => void;
  onLoaded?: (data: SelectedPortfolioTemplateDTO[]) => void;
}
export const PortfolioTemplate = ({
  isDisabled,
  portfolioSize,
  replaceInvestment,
  onReplaceData,
  onClearSaveAsTemplateWarnings,
  onLoaded,
}: PortfolioTemplateProps) => {
  const { portfolioTemplates } = useSelector(
    (state: RootState) => state.managedAccount,
  );

  const {
    originalInvestmentSelections,
    originalInvestmentFavourited,
    setInvestmentSelection,
    setInvestmentFavourited,
  } = useInvestment();

  const {
    managedAccountId,
    loadManagedAccountPortfolioTemplates,
    loadManagedAccountPortfolioTemplateById,
  } = useManagedAccount();

  const loadData = async () => {
    return await loadManagedAccountPortfolioTemplates();
  };

  const loadDataPortfolioTemplate = async (portfolioTemplateId: string) => {
    const response = await loadManagedAccountPortfolioTemplateById(
      managedAccountId ?? "new",
      portfolioTemplateId,
      portfolioSize,
    );

    const { selectedInvestments, feeCalculatorData } = response ?? {};

    replaceInvestment?.(selectedInvestments ?? []);
    onReplaceData?.(selectedInvestments ?? [], feeCalculatorData ?? []);

    setInvestmentSelection(originalInvestmentSelections);
    setInvestmentFavourited(originalInvestmentFavourited);

    onClearSaveAsTemplateWarnings();
  };

  useEffectOnce(() => {
    loadData().then((data) => {
      if (data && onLoaded) onLoaded(data);
    });
  });

  return (
    <SHAutocomplete
      disabled={isDisabled}
      textFieldProps={{
        InputLabelProps: { shrink: true },
        label: "Select a template to load",
        placeholder: "Search for a template",
      }}
      options={portfolioTemplates ?? []}
      getOptionLabel={(option: SelectedPortfolioTemplateDTO) =>
        option.name ?? ""
      }
      onChange={async (_, value) => {
        if (value) {
          await loadDataPortfolioTemplate(value?.id ?? "");
        }
      }}
      renderOption={(props, option, { selected }) => {
        return (
          <li {...props} key={option.id} style={{ padding: "1px 10px" }}>
            <SHStack alignItems={"center"} direction="row" spacing={1}>
              <SHRadio checked={selected} />
              <SHTypography variant="body3" fontWeight={500}>
                {option.name}
              </SHTypography>
              {option.note && <InformationButton content={option.note} />}
            </SHStack>
          </li>
        );
      }}
      sx={{ width: { xs: "100%", md: 520 } }}
    />
  );
};
